// Gemeinsamer Store für In-App-Benachrichtigungen (Mitteilungszentrum). Hält die
// Notification-Liste EINMAL (Initial-Load + 60-s-Poll + Realtime-Channel
// 'notification') und stellt sie Glocke (NavigationLayout), Dashboard-Karte und
// Toasts synchron bereit — so wirken Lesen/Leeren überall sofort, statt dass
// jede Komponente eigenständig fetcht (vorher driftete die Dashboard-Karte).
import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { notificationService, type NotificationDto } from "../services/notificationService";
import { useAuth } from "./AuthContext";
import { useRealtimeOptional } from "./RealtimeContext";

export interface NotificationContextValue {
    /** Alle eigenen Benachrichtigungen, neueste zuerst. */
    notifications: NotificationDto[];
    /** Liste neu vom Server laden. */
    reload: () => void;
    /** Eine als gelesen markieren (optimistisch + Server). */
    markRead: (id: number) => Promise<void>;
    /** Alle als gelesen markieren (optimistisch + Server) — „Leeren". */
    markAllRead: () => Promise<void>;
    /** Mehrere als gelesen markieren — „Alle abhaken" einer Gruppe/eines Abschnitts. */
    markManyRead: (ids: readonly number[]) => Promise<void>;
}

const Ctx = createContext<NotificationContextValue | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const realtime = useRealtimeOptional();
    const [notifications, setNotifications] = useState<NotificationDto[]>([]);

    const reload = useCallback(() => {
        notificationService
            .listMine()
            .then(setNotifications)
            .catch(() => undefined);
    }, []);

    // Initial-Load + 60-s-Poll (Fallback gegen verlorene Realtime-Frames).
    useEffect(() => {
        if (!isAuthenticated) {
            setNotifications([]);
            return;
        }
        reload();
        // Versteckter Tab: nicht pollen; beim Zurückkehren einmal nachladen.
        const t = window.setInterval(() => {
            if (!document.hidden) reload();
        }, 60_000);
        const onVis = () => {
            if (!document.hidden) reload();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            window.clearInterval(t);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [isAuthenticated, reload]);

    // Live: 'notification'-Frames koaleszieren (M5) — eine Welle löst EINEN Reload
    // (nach 400 ms) aus, statt eines Vollreloads pro Frame (Reload-Amplifikation).
    useEffect(() => {
        if (!isAuthenticated || !realtime) return;
        let timer: number | null = null;
        const unsub = realtime.subscribe("notification", () => {
            if (timer !== null) return;
            timer = window.setTimeout(() => {
                timer = null;
                reload();
            }, 400);
        });
        return () => {
            if (timer !== null) window.clearTimeout(timer);
            unsub();
        };
    }, [isAuthenticated, realtime, reload]);

    const markRead = useCallback(async (id: number) => {
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n)),
        );
        try {
            await notificationService.markRead(id);
        } catch {
            /* optimistisch — der nächste Poll korrigiert ggf. */
        }
    }, []);

    const markAllRead = useCallback(async () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
        try {
            await notificationService.markAllRead();
        } catch {
            /* optimistisch */
        }
    }, []);

    const markManyRead = useCallback(async (ids: readonly number[]) => {
        if (!ids.length) return;
        const set = new Set(ids);
        const jetzt = new Date().toISOString();
        setNotifications((prev) => prev.map((n) => (set.has(n.id) ? { ...n, readAt: n.readAt ?? jetzt } : n)));
        // Kein Sammel-Endpunkt — je Mitteilung ein PATCH, parallel; Fehler korrigiert der nächste Poll.
        await Promise.allSettled([...set].map((id) => notificationService.markRead(id)));
    }, []);

    const value: NotificationContextValue = { notifications, reload, markRead, markAllRead, markManyRead };
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/** Zugriff auf den gemeinsamen Notification-Store (wirft ohne Provider). */
export const useNotifications = (): NotificationContextValue => {
    const c = useContext(Ctx);
    if (!c) throw new Error("useNotifications must be used within a NotificationProvider");
    return c;
};
